import { useEffect, useMemo, useState } from "react";
import { api, fmtEur, fmtDate, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Wallet, Clock, Coins, Plus, Pencil, Trash2, HandCoins, Store } from "lucide-react";

const STATO = {
  pagato: { label: "Pagata", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  non_pagato: { label: "Non Pagata", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  in_sospeso: { label: "In Sospeso", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { data: today(), partner_id: "", importo: "", numero_fattura: "", stato_pagamento: "pagato", nota: "" };
const EMPTY_RINV = { data: today(), partner_id: "", importo: "", numero_fattura: "", stato_pagamento: "non_pagato", nota: "" };

export default function Payments() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const isRivenditore = user.role === "partner" && user.tipo_partner === "rivenditore";
  const showReseller = isOwner || isRivenditore;
  const [data, setData] = useState(null);
  const [roy, setRoy] = useState(null);
  const [payments, setPayments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [period, setPeriod] = useState({ dal: "", al: "" });
  const [rinv, setRinv] = useState(null);
  const [rinvoices, setRinvoices] = useState([]);
  const [rOpen, setROpen] = useState(false);
  const [rForm, setRForm] = useState(EMPTY_RINV);
  const [rEditId, setREditId] = useState(null);

  const loadRoyalties = () => {
    const params = {};
    if (period.dal) params.dal = period.dal;
    if (period.al) params.al = period.al;
    api.get("/royalties/summary", { params }).then((r) => setRoy(r.data));
    api.get("/royalty-payments", { params }).then((r) => setPayments(r.data));
  };
  const loadReseller = () => {
    if (!showReseller) return;
    const params = {};
    if (period.dal) params.dal = period.dal;
    if (period.al) params.al = period.al;
    api.get("/reseller-invoices/summary", { params }).then((r) => setRinv(r.data)).catch(() => {});
    api.get("/reseller-invoices", { params }).then((r) => setRinvoices(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/payments/summary").then((r) => setData(r.data));
    if (isOwner) api.get("/partners").then((r) => setPartners(r.data));
  }, [isOwner]);
  useEffect(loadRoyalties, [period]);
  useEffect(loadReseller, [period]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ data: p.data, partner_id: p.partner_id, importo: p.importo, numero_fattura: p.numero_fattura || "", stato_pagamento: p.stato_pagamento, nota: p.nota || "" });
    setEditId(p.id); setOpen(true);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openRNew = () => { setRForm(EMPTY_RINV); setREditId(null); setROpen(true); };
  const openREdit = (p) => {
    setRForm({ data: p.data, partner_id: p.partner_id, importo: p.importo, numero_fattura: p.numero_fattura || "", stato_pagamento: p.stato_pagamento, nota: p.nota || "" });
    setREditId(p.id); setROpen(true);
  };
  const setR = (k) => (e) => setRForm({ ...rForm, [k]: e.target.value });

  const saveReseller = async () => {
    if (!rForm.partner_id) return toast.error("Seleziona il rivenditore");
    try {
      const payload = {
        data: rForm.data, partner_id: rForm.partner_id, importo: parseFloat(rForm.importo) || 0,
        numero_fattura: rForm.numero_fattura, stato_pagamento: rForm.stato_pagamento, nota: rForm.nota,
      };
      if (rEditId) await api.put(`/reseller-invoices/${rEditId}`, payload);
      else await api.post("/reseller-invoices", payload);
      toast.success(rEditId ? "Fattura aggiornata" : "Fattura al rivenditore registrata");
      setROpen(false); loadReseller();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delReseller = async (id) => {
    if (!window.confirm("Eliminare questa fattura?")) return;
    await api.delete(`/reseller-invoices/${id}`); toast.success("Fattura eliminata"); loadReseller();
  };
  const rivenditori = useMemo(() => partners.filter((p) => p.tipo_partner === "rivenditore"), [partners]);

  const save = async () => {
    if (!form.partner_id) return toast.error("Seleziona il partner");
    try {
      const payload = {
        data: form.data, partner_id: form.partner_id, importo: parseFloat(form.importo) || 0,
        numero_fattura: form.numero_fattura, stato_pagamento: form.stato_pagamento, nota: form.nota,
      };
      if (editId) await api.put(`/royalty-payments/${editId}`, payload);
      else await api.post("/royalty-payments", payload);
      toast.success(editId ? "Pagamento aggiornato" : "Pagamento royalty registrato");
      setOpen(false); loadRoyalties();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo pagamento?")) return;
    await api.delete(`/royalty-payments/${id}`); toast.success("Pagamento eliminato"); loadRoyalties();
  };

  const sibillCards = useMemo(() => data ? [
    { id: "pay-incassato", label: "Incassato (Sibill)", value: data.incassato, icon: Wallet, tint: "bg-emerald-50 text-emerald-600" },
    { id: "pay-da-incassare", label: "Da Incassare (Sibill)", value: data.da_incassare, icon: Clock, tint: "bg-rose-50 text-rose-600" },
    { id: "pay-totale", label: "Totale Fatture", value: data.totale, icon: Coins, tint: "bg-dusty-50 text-dusty-600" },
  ] : [], [data]);

  if (!data || !roy) return <div className="text-slate-400">Caricamento…</div>;

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Stato Pagamenti</h1>
        <p className="text-sm text-slate-500 mt-1">Incassi dai clienti e pagamenti delle royalties ai partner.</p>
      </div>

      {/* Royalty ai partner (non mostrata ai rivenditori) */}
      {!isRivenditore && (
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-semibold text-slate-800 flex items-center gap-2"><HandCoins className="h-5 w-5 text-dusty-600" /> Royalties ai Partner</h2>
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-xs text-slate-500">Dal</Label>
              <Input type="date" value={period.dal} onChange={(e) => setPeriod({ ...period, dal: e.target.value })} data-testid="royalty-filter-dal" className="mt-1 h-9 bg-white" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Al</Label>
              <Input type="date" value={period.al} onChange={(e) => setPeriod({ ...period, al: e.target.value })} data-testid="royalty-filter-al" className="mt-1 h-9 bg-white" />
            </div>
            {(period.dal || period.al) && (
              <Button variant="ghost" className="h-9 text-slate-500" onClick={() => setPeriod({ dal: "", al: "" })} data-testid="royalty-filter-clear">Azzera</Button>
            )}
            {isOwner && (
              <Button onClick={openNew} data-testid="add-royalty-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white h-9">
                <Plus className="h-4 w-4 mr-1.5" /> Registra pagamento
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-5 border-slate-200 shadow-sm" data-testid="roy-maturata">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Royalty Maturate</div>
            <div className="mt-2 font-display text-2xl font-bold text-slate-900">{fmtEur(roy.totale_maturata)}</div>
          </Card>
          <Card className="p-5 border-slate-200 shadow-sm" data-testid="roy-pagata">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Royalty Pagate</div>
            <div className="mt-2 font-display text-2xl font-bold text-emerald-700">{fmtEur(roy.totale_pagata)}</div>
          </Card>
          <Card className="p-5 border-slate-200 shadow-sm" data-testid="roy-residuo">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Residuo da Pagare</div>
            <div className="mt-2 font-display text-2xl font-bold text-rose-600">{fmtEur(roy.totale_residuo)}</div>
          </Card>
        </div>

        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100"><h3 className="font-medium text-slate-700 text-sm">Riepilogo maturato vs pagato</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-100/70 border-b border-slate-200">
                  <th className="py-3 px-4">Partner</th>
                  <th className="py-3 px-4 text-right">Maturata</th>
                  <th className="py-3 px-4 text-right">Pagata</th>
                  <th className="py-3 px-4 text-right">Residuo</th>
                </tr>
              </thead>
              <tbody>
                {roy.per_partner.map((p) => (
                  <tr key={p.partner_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`roy-partner-${p.partner_id}`}>
                    <td className="py-3 px-4 font-medium text-slate-800">{p.nome}</td>
                    <td className="py-3 px-4 text-right text-slate-600">{fmtEur(p.maturata)}</td>
                    <td className="py-3 px-4 text-right text-emerald-700">{fmtEur(p.pagata)}</td>
                    <td className={`py-3 px-4 text-right font-semibold ${p.residuo > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtEur(p.residuo)}</td>
                  </tr>
                ))}
                {roy.per_partner.length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-slate-400">Nessuna royalty registrata</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100"><h3 className="font-medium text-slate-700 text-sm">Pagamenti registrati (riferimenti di fatturazione)</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-100/70 border-b border-slate-200">
                  <th className="py-3 px-4">Data</th>
                  <th className="py-3 px-4">Partner</th>
                  <th className="py-3 px-4">N. Fattura</th>
                  <th className="py-3 px-4 text-right">Importo</th>
                  <th className="py-3 px-4">Stato</th>
                  <th className="py-3 px-4">Nota</th>
                  {isOwner && <th className="py-3 px-4"></th>}
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const st = STATO[p.stato_pagamento] || STATO.in_sospeso;
                  return (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`royalty-row-${p.id}`}>
                      <td className="py-3 px-4 text-slate-600 whitespace-nowrap">{fmtDate(p.data)}</td>
                      <td className="py-3 px-4 font-medium text-slate-800">{p.partner_nome}</td>
                      <td className="py-3 px-4 text-slate-600">{p.numero_fattura || "—"}</td>
                      <td className="py-3 px-4 text-right font-medium text-slate-800">{fmtEur(p.importo)}</td>
                      <td className="py-3 px-4"><Badge className={`${st.cls} border hover:opacity-100`}>{st.label}</Badge></td>
                      <td className="py-3 px-4 text-slate-500 max-w-xs truncate">{p.nota || "—"}</td>
                      {isOwner && (
                        <td className="py-3 px-4">
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-royalty-${p.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => del(p.id)} data-testid={`delete-royalty-${p.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {payments.length === 0 && (
                  <tr><td colSpan={isOwner ? 7 : 6} className="py-8 text-center text-slate-400">Nessun pagamento royalty registrato</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </section>
      )}

      {/* Fatture ai Rivenditori (ICS -> rivenditore) */}
      {showReseller && rinv && (
      <section className="space-y-4" data-testid="reseller-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-semibold text-slate-800 flex items-center gap-2"><Store className="h-5 w-5 text-violet-600" /> Fatture ai Rivenditori</h2>
          {isOwner && (
            <Button onClick={openRNew} data-testid="add-reseller-invoice-btn" className="bg-violet-600 hover:bg-violet-700 text-white h-9">
              <Plus className="h-4 w-4 mr-1.5" /> Registra fattura
            </Button>
          )}
        </div>
        <p className="text-sm text-slate-500 -mt-2">Fatture emesse da Associazione I.C.S. ai partner rivenditori (imponibile + IVA). Il rivenditore paga l'importo richiesto.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-5 border-slate-200 shadow-sm" data-testid="rinv-dovuto">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Dovuto (calcolato)</div>
            <div className="mt-2 font-display text-2xl font-bold text-slate-900">{fmtEur(rinv.totale_dovuto)}</div>
          </Card>
          <Card className="p-5 border-slate-200 shadow-sm" data-testid="rinv-fatturato">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Fatturato</div>
            <div className="mt-2 font-display text-2xl font-bold text-violet-700">{fmtEur(rinv.totale_fatturato)}</div>
          </Card>
          <Card className="p-5 border-slate-200 shadow-sm" data-testid="rinv-pagato">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Pagato</div>
            <div className="mt-2 font-display text-2xl font-bold text-emerald-700">{fmtEur(rinv.totale_pagato)}</div>
          </Card>
          <Card className="p-5 border-slate-200 shadow-sm" data-testid="rinv-residuo">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Residuo da Incassare</div>
            <div className="mt-2 font-display text-2xl font-bold text-rose-600">{fmtEur(rinv.totale_residuo)}</div>
          </Card>
        </div>

        {isOwner && (
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100"><h3 className="font-medium text-slate-700 text-sm">Riepilogo per rivenditore</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-100/70 border-b border-slate-200">
                    <th className="py-3 px-4">Rivenditore</th>
                    <th className="py-3 px-4 text-right">Dovuto</th>
                    <th className="py-3 px-4 text-right">Fatturato</th>
                    <th className="py-3 px-4 text-right">Pagato</th>
                    <th className="py-3 px-4 text-right">Residuo</th>
                  </tr>
                </thead>
                <tbody>
                  {rinv.per_partner.map((p) => (
                    <tr key={p.partner_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`rinv-partner-${p.partner_id}`}>
                      <td className="py-3 px-4 font-medium text-slate-800">{p.nome}</td>
                      <td className="py-3 px-4 text-right text-slate-600">{fmtEur(p.dovuto)}</td>
                      <td className="py-3 px-4 text-right text-violet-700">{fmtEur(p.fatturato)}</td>
                      <td className="py-3 px-4 text-right text-emerald-700">{fmtEur(p.pagato)}</td>
                      <td className={`py-3 px-4 text-right font-semibold ${p.residuo > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtEur(p.residuo)}</td>
                    </tr>
                  ))}
                  {rinv.per_partner.length === 0 && (
                    <tr><td colSpan={5} className="py-8 text-center text-slate-400">Nessun rivenditore con vendite o fatture</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100"><h3 className="font-medium text-slate-700 text-sm">Fatture registrate</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-100/70 border-b border-slate-200">
                  <th className="py-3 px-4">Data</th>
                  {isOwner && <th className="py-3 px-4">Rivenditore</th>}
                  <th className="py-3 px-4">N. Fattura</th>
                  <th className="py-3 px-4 text-right">Importo</th>
                  <th className="py-3 px-4">Stato</th>
                  <th className="py-3 px-4">Nota</th>
                  {isOwner && <th className="py-3 px-4"></th>}
                </tr>
              </thead>
              <tbody>
                {rinvoices.map((p) => {
                  const st = STATO[p.stato_pagamento] || STATO.in_sospeso;
                  return (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`rinv-row-${p.id}`}>
                      <td className="py-3 px-4 text-slate-600 whitespace-nowrap">{fmtDate(p.data)}</td>
                      {isOwner && <td className="py-3 px-4 font-medium text-slate-800">{p.partner_nome}</td>}
                      <td className="py-3 px-4 text-slate-600">{p.numero_fattura || "—"}</td>
                      <td className="py-3 px-4 text-right font-medium text-slate-800">{fmtEur(p.importo)}</td>
                      <td className="py-3 px-4"><Badge className={`${st.cls} border hover:opacity-100`}>{st.label}</Badge></td>
                      <td className="py-3 px-4 text-slate-500 max-w-xs truncate">{p.nota || "—"}</td>
                      {isOwner && (
                        <td className="py-3 px-4">
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" onClick={() => openREdit(p)} data-testid={`edit-rinv-${p.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => delReseller(p.id)} data-testid={`delete-rinv-${p.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {rinvoices.length === 0 && (
                  <tr><td colSpan={isOwner ? 7 : 5} className="py-8 text-center text-slate-400">Nessuna fattura registrata</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </section>
      )}
      <section className="space-y-4">
        <h2 className="font-display text-lg font-semibold text-slate-800">Incassi Clienti (da Sibill)</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {sibillCards.map((c) => (
            <Card key={c.id} className="p-5 border-slate-200 shadow-sm" data-testid={c.id}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">{c.label}</div>
                  <div className="mt-2 font-display text-2xl font-bold text-slate-900">{fmtEur(c.value)}</div>
                </div>
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${c.tint}`}><c.icon className="h-5 w-5" /></div>
              </div>
            </Card>
          ))}
        </div>
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100"><h3 className="font-medium text-slate-700 text-sm">Incassi per Partner</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-100/70 border-b border-slate-200">
                  <th className="py-3 px-4">Partner</th>
                  <th className="py-3 px-4 text-right">Incassato</th>
                  <th className="py-3 px-4 text-right">Da Incassare</th>
                </tr>
              </thead>
              <tbody>
                {data.per_partner.map((p, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`payment-partner-${i}`}>
                    <td className="py-3 px-4 font-medium text-slate-800">{p.nome}</td>
                    <td className="py-3 px-4 text-right text-emerald-700 font-medium">{fmtEur(p.incassato)}</td>
                    <td className="py-3 px-4 text-right text-rose-600 font-medium">{fmtEur(p.da_incassare)}</td>
                  </tr>
                ))}
                {data.per_partner.length === 0 && (
                  <tr><td colSpan={3} className="py-8 text-center text-slate-400">Nessun dato. Importa fatture da Sibill.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="royalty-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Modifica Pagamento Royalty" : "Registra Pagamento Royalty"}</DialogTitle>
            <DialogDescription>Inserisci i riferimenti di fatturazione del pagamento della royalty al partner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data</Label><Input type="date" value={form.data} onChange={set("data")} data-testid="royalty-data" className="mt-1.5" /></div>
              <div><Label>Partner</Label>
                <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="royalty-partner"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Importo (€)</Label><Input type="number" step="0.01" value={form.importo} onChange={set("importo")} data-testid="royalty-importo" className="mt-1.5" /></div>
              <div><Label>N. Fattura / Riferimento</Label><Input value={form.numero_fattura} onChange={set("numero_fattura")} data-testid="royalty-numero-fattura" placeholder="es. FT 2026/45" className="mt-1.5" /></div>
            </div>
            <div>
              <Label>Stato pagamento</Label>
              <Select value={form.stato_pagamento} onValueChange={(v) => setForm({ ...form, stato_pagamento: v })}>
                <SelectTrigger className="mt-1.5" data-testid="royalty-stato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pagato">Pagata</SelectItem>
                  <SelectItem value="non_pagato">Non Pagata</SelectItem>
                  <SelectItem value="in_sospeso">In Sospeso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Nota</Label><Textarea value={form.nota} onChange={set("nota")} data-testid="royalty-nota" placeholder="Annotazioni…" className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="royalty-save" className="bg-dusty-600 hover:bg-dusty-700 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rOpen} onOpenChange={setROpen}>
        <DialogContent className="bg-white" data-testid="reseller-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{rEditId ? "Modifica Fattura Rivenditore" : "Registra Fattura al Rivenditore"}</DialogTitle>
            <DialogDescription>Fattura emessa da Associazione I.C.S. al rivenditore (imponibile + IVA): l'importo richiesto al partner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data</Label><Input type="date" value={rForm.data} onChange={setR("data")} data-testid="rinv-data" className="mt-1.5" /></div>
              <div><Label>Rivenditore</Label>
                <Select value={rForm.partner_id} onValueChange={(v) => {
                  const p = rinv?.per_partner?.find((x) => x.partner_id === v);
                  const daFatturare = p ? Math.max(0, (p.dovuto || 0) - (p.fatturato || 0)) : 0;
                  setRForm((f) => ({ ...f, partner_id: v, importo: daFatturare > 0 ? daFatturare : f.importo }));
                }}>
                  <SelectTrigger className="mt-1.5" data-testid="rinv-partner"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {rivenditori.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Nessun partner rivenditore</div>}
                    {rivenditori.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(() => {
              const p = rinv?.per_partner?.find((x) => x.partner_id === rForm.partner_id);
              if (!p) return null;
              const daFatturare = Math.max(0, (p.dovuto || 0) - (p.fatturato || 0));
              return (
                <div className="rounded-md bg-violet-50 border border-violet-200 p-2.5 text-xs text-violet-800 flex items-center justify-between" data-testid="rinv-dovuto-hint">
                  <span>Dovuto dalle vendite: <b>{fmtEur(p.dovuto)}</b> · Già fatturato: <b>{fmtEur(p.fatturato)}</b> · Da fatturare: <b>{fmtEur(daFatturare)}</b></span>
                  <button type="button" onClick={() => setRForm((f) => ({ ...f, importo: daFatturare }))} data-testid="rinv-use-dovuto" className="ml-2 shrink-0 font-semibold underline hover:no-underline">Usa importo</button>
                </div>
              );
            })()}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Importo richiesto (€, imponibile + IVA)</Label><Input type="number" step="0.01" value={rForm.importo} onChange={setR("importo")} data-testid="rinv-importo" className="mt-1.5" /></div>
              <div><Label>N. Fattura</Label><Input value={rForm.numero_fattura} onChange={setR("numero_fattura")} data-testid="rinv-numero-fattura" placeholder="es. FT 2026/12" className="mt-1.5" /></div>
            </div>
            <div>
              <Label>Stato pagamento</Label>
              <Select value={rForm.stato_pagamento} onValueChange={(v) => setRForm({ ...rForm, stato_pagamento: v })}>
                <SelectTrigger className="mt-1.5" data-testid="rinv-stato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pagato">Pagata</SelectItem>
                  <SelectItem value="non_pagato">Non Pagata</SelectItem>
                  <SelectItem value="in_sospeso">In Sospeso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Nota</Label><Textarea value={rForm.nota} onChange={setR("nota")} data-testid="rinv-nota" placeholder="Annotazioni…" className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setROpen(false)}>Annulla</Button>
            <Button onClick={saveReseller} data-testid="rinv-save" className="bg-violet-600 hover:bg-violet-700 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
